import { logError, logWarn } from '@ptek/client-logger';

/**
 * Byte bound on un-acked buffered audio (~1.7 h @ 128 kbps). Reaching it drops
 * the OLDEST un-acked chunk with a loud counted error — never silently, and
 * recording never stops (AC5/AC3).
 */
export const MAX_UNACKED_BUFFER_BYTES = 100 * 1024 * 1024;
export const BACKOFF_BASE_MS = 1000;
export const BACKOFF_MAX_MS = 30_000;
/** Safety-net retry cadence, independent of backoff timers and `online`. */
export const RETRY_INTERVAL_MS = 30_000;

export interface UploadQueueCounters {
  /**
   * Chunks whose audio was LOST at the byte bound: never uploaded, so their
   * audio reaches no server. Real, reportable data loss.
   */
  droppedAtBound: number;
  /**
   * Chunks whose bytes were freed at the byte bound AFTER a successful upload.
   * The server already owns the audio, so this is only memory being reclaimed —
   * it must never be read as audio loss.
   */
  releasedAtBound: number;
  nonRetryableFailures: number;
  retryRecoveries: number;
}

const counters: UploadQueueCounters = {
  droppedAtBound: 0,
  releasedAtBound: 0,
  nonRetryableFailures: 0,
  retryRecoveries: 0,
};

export function getUploadQueueCounters(): UploadQueueCounters {
  return { ...counters };
}

export function resetUploadQueueCounters(): void {
  counters.droppedAtBound = 0;
  counters.releasedAtBound = 0;
  counters.nonRetryableFailures = 0;
  counters.retryRecoveries = 0;
}

/**
 * How many times a 403 may be retried before it is treated as permanent. The
 * queue is a single ordered cursor, so an unbounded 403 retry blocks every
 * chunk behind it for the rest of the visit; a genuinely permanent 403 (a chunk
 * posted at a foreign practice's session) has to clear the head of line.
 */
export const FORBIDDEN_MAX_ATTEMPTS = 3;

/**
 * How many times a 401 may be retried before it is treated as permanent. A
 * token that never refreshes would otherwise retry forever at the safety-net
 * cadence, so the run never settles and a stop's flush never returns. The
 * bound is generous — the retry schedule spends roughly a minute of wall clock
 * here — because the host's token getter is re-invoked on every attempt, so a
 * refresh landing at any point within it recovers the rest of the audio.
 */
export const UNAUTHORIZED_MAX_ATTEMPTS = 10;

/**
 * A 4xx (other than the transient ones below) can never succeed on replay, so
 * retrying would storm the server with a poisoned chunk; network errors,
 * timeouts, and 5xx are transient. Classified by the error's `status` property
 * (duck-typed: upload errors cross a module-mock boundary in tests, so
 * instanceof is not reliable); no status means no HTTP response — retryable.
 *
 * 401/403 are transient here specifically because chunk upload is a raw XHR
 * with no refresh-and-retry wrapper: an access token that expires mid-visit
 * fails every remaining chunk of the recording, and treating that as permanent
 * would silently drop the rest of the encounter's audio. A retry after the
 * token refreshes succeeds. 404 stays permanent — the session does not exist.
 *
 * 403 is BOUNDED by {@link FORBIDDEN_MAX_ATTEMPTS} because it is also how an
 * authorization decision that will never change is reported, and 401 by
 * {@link UNAUTHORIZED_MAX_ATTEMPTS} because a token that never refreshes would
 * otherwise retry forever. `attempts` is the count INCLUDING the attempt that
 * just failed; omit it (the default) to ask the question without attempt
 * context, which keeps both retryable.
 */
export function isRetryableUploadError(error: unknown, attempts = 0): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  if (typeof status !== 'number') {
    return true;
  }
  if (status === 403) {
    return attempts < FORBIDDEN_MAX_ATTEMPTS;
  }
  if (status === 401) {
    return attempts < UNAUTHORIZED_MAX_ATTEMPTS;
  }
  if (status === 408 || status === 429) {
    return true;
  }
  return status < 400 || status >= 500;
}

export type ChunkSettleOutcome = 'uploaded' | 'failed' | 'dropped';

export interface QueuedChunk<TRun> {
  /**
   * Mutable ONLY via {@link UploadQueue.shiftRunChunkNumbers}, and only before
   * the chunk's first upload attempt.
   */
  chunkNumber: number;
  readonly size: number;
  readonly captureEndMs: number;
  /** The recording run this chunk belongs to, stamped at enqueue. */
  readonly run: TRun;
  /** Null once released — the queue no longer retains the bytes. */
  blob: Blob | null;
  isFinal: boolean;
  /** True when an attempt that carried the final marker succeeded. */
  finalDelivered: boolean;
  status: 'queued' | 'uploading' | 'uploaded' | 'failed' | 'dropped';
  released: boolean;
  /** Set when the transcript arrived before the upload settled. */
  transcriptReceived: boolean;
  attempts: number;
}

export interface UploadQueueOptions<TRun> {
  /** One upload attempt. Rejections are classified via isRetryableUploadError. */
  upload(chunk: QueuedChunk<TRun>): Promise<void>;
  /** Gate: false means the chunk's run cannot upload yet (session pending). */
  canUpload(chunk: QueuedChunk<TRun>): boolean;
  /**
   * Release policy: true releases the chunk's bytes on upload success (the
   * server owns durability from the 200); false holds them until
   * releaseForTranscript confirms durability.
   */
  releaseOnUpload(chunk: QueuedChunk<TRun>): boolean;
  /** Fired exactly once per chunk when it reaches a terminal outcome. */
  onSettled(
    chunk: QueuedChunk<TRun>,
    outcome: ChunkSettleOutcome,
    error?: unknown
  ): void;
  /** First retryable failure after healthy operation. */
  onDegraded?(chunk: QueuedChunk<TRun>): void;
  /** First success after a degraded period. */
  onRecovered?(chunk: QueuedChunk<TRun>): void;
  /** Browser came back online (fired before the queue pumps). */
  onOnline?(): void;
  maxUnackedBufferBytes?: number;
}

/**
 * Single ordered uploader for one transport session: chunks upload in strictly
 * ascending chunk-number order with at most one attempt in flight, surviving
 * outages via exponential backoff with full jitter, the browser `online`
 * event, and a periodic safety-net interval. A non-retryable failure settles
 * the chunk as failed and the queue continues with the next chunk — a hole in
 * delivery is server-visible via expectedChunkCount; the queue never wedges on
 * a poisoned chunk.
 */
export class UploadQueue<TRun> {
  private readonly entries: QueuedChunk<TRun>[] = [];
  private cursor = 0;
  private unackedBytes = 0;
  private readonly maxUnackedBytes: number;
  private uploading = false;
  private held = false;
  private closed = false;
  private disposed = false;
  private degraded = false;
  private readonly runWaiters: Array<{ run: TRun; resolve: () => void }> = [];
  private backoffTimer: number | null = null;
  private readonly retryInterval: number;
  private readonly handleOnline = (): void => {
    if (this.backoffTimer !== null) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
    this.options.onOnline?.();
    this.kick();
  };

  constructor(private readonly options: UploadQueueOptions<TRun>) {
    this.maxUnackedBytes =
      options.maxUnackedBufferBytes ?? MAX_UNACKED_BUFFER_BYTES;
    window.addEventListener('online', this.handleOnline);
    this.retryInterval = window.setInterval(
      () => this.kick(),
      RETRY_INTERVAL_MS
    );
  }

  enqueue(init: {
    chunkNumber: number;
    blob: Blob;
    size: number;
    captureEndMs: number;
    run: TRun;
  }): QueuedChunk<TRun> {
    if (this.closed || this.disposed) {
      logWarn('uploadQueue', 'Chunk enqueued after queue closed; dropping', {
        chunkNumber: init.chunkNumber,
      });
      const entry: QueuedChunk<TRun> = {
        chunkNumber: init.chunkNumber,
        size: init.size,
        captureEndMs: init.captureEndMs,
        run: init.run,
        blob: null,
        isFinal: false,
        finalDelivered: false,
        status: 'dropped',
        released: true,
        transcriptReceived: false,
        attempts: 0,
      };
      this.options.onSettled(entry, 'dropped');
      return entry;
    }
    const entry: QueuedChunk<TRun> = {
      chunkNumber: init.chunkNumber,
      size: init.size,
      captureEndMs: init.captureEndMs,
      run: init.run,
      blob: init.blob,
      isFinal: false,
      finalDelivered: false,
      status: 'queued',
      released: false,
      transcriptReceived: false,
      attempts: 0,
    };
    this.evictForBytes(entry.size);
    this.entries.push(entry);
    this.unackedBytes += entry.size;
    // A drop at the bound is a terminal outcome, so it can complete a pending
    // run-settled wait; run it AFTER the push so the incoming chunk is counted.
    this.notifyRunWaiters();
    this.kick();
    return entry;
  }

  /** Mark the run's tail chunk so its next upload attempt carries ?final=true. */
  markFinal(chunkNumber: number): QueuedChunk<TRun> | null {
    const entry = this.entries.find((e) => e.chunkNumber === chunkNumber);
    if (!entry) {
      return null;
    }
    entry.isFinal = true;
    return entry;
  }

  /**
   * Lift every chunk of `run` by `offset` chunk positions, because the server's
   * authoritative cursor overtook the local counter (a reloaded browser numbers
   * from 0 again). Ascending order is preserved — every entry of the run shifts
   * by the same amount, and the run's numbers were the highest in the queue.
   *
   * Throws if any of the run's chunks has already been attempted: renumbering a
   * chunk the server has seen would orphan its S3 object, so this must fail
   * visibly rather than half-apply.
   */
  shiftRunChunkNumbers(run: TRun, offset: number): void {
    for (const entry of this.entries) {
      if (entry.run !== run) {
        continue;
      }
      if (entry.attempts > 0) {
        throw new Error(
          `Cannot renumber chunk ${entry.chunkNumber}: already attempted`
        );
      }
    }
    for (const entry of this.entries) {
      if (entry.run === run) {
        entry.chunkNumber += offset;
      }
    }
  }

  /** Pause uploads (the stop path holds while it final-marks the tail). */
  hold(): void {
    this.held = true;
  }

  releaseHold(): void {
    this.held = false;
    this.kick();
  }

  /**
   * Hold-until-transcript release (non-unified runs): the chunk's transcript
   * arrived, so the server has proven durability end to end.
   */
  releaseForTranscript(chunkNumber: number): void {
    const entry = this.entries.find((e) => e.chunkNumber === chunkNumber);
    if (!entry || entry.released) {
      return;
    }
    if (entry.status === 'uploaded') {
      this.release(entry);
      this.maybeDispose();
    } else {
      entry.transcriptReceived = true;
    }
  }

  /**
   * Settle a run's not-yet-attempted chunks as failed (the run's upload
   * session could not be established, so they can never upload). The caller
   * owns the aggregate error log; the optional error preserves its cause for
   * per-chunk reporting.
   */
  failPending(run: TRun, error?: unknown): number {
    let count = 0;
    for (const entry of this.entries) {
      if (entry.run === run && entry.status === 'queued') {
        entry.status = 'failed';
        this.release(entry);
        count += 1;
        this.options.onSettled(entry, 'failed', error);
      }
    }
    this.notifyRunWaiters();
    this.maybeDispose();
    return count;
  }

  /**
   * No further chunks will be enqueued (the transport session was superseded).
   * The queue keeps draining what it has, releases transcript-held bytes —
   * their confirmation channel dies with the session — and self-disposes once
   * everything has settled.
   */
  close(): void {
    this.closed = true;
    for (const entry of this.entries) {
      if (entry.status === 'uploaded' && !entry.released) {
        this.release(entry);
      }
    }
    this.maybeDispose();
  }

  /**
   * Resolve once every chunk enqueued by `run` has settled — i.e. the server has
   * acknowledged (or permanently rejected) that run's tail. Chunks belonging to
   * a LATER run are deliberately not waited on: the queue is shared across takes
   * of one transport session, so waiting on queue-wide idleness would keep a
   * pause's flush open for as long as a resumed recording keeps producing audio.
   */
  waitForRunSettled(run: TRun): Promise<void> {
    if (this.isRunSettled(run)) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.runWaiters.push({ run, resolve });
    });
  }

  private isRunSettled(run: TRun): boolean {
    return this.entries.every(
      (entry) =>
        entry.run !== run ||
        entry.status === 'uploaded' ||
        entry.status === 'failed' ||
        entry.status === 'dropped'
    );
  }

  private notifyRunWaiters(): void {
    for (let index = this.runWaiters.length - 1; index >= 0; index -= 1) {
      const waiter = this.runWaiters[index];
      if (this.isRunSettled(waiter.run)) {
        this.runWaiters.splice(index, 1);
        waiter.resolve();
      }
    }
  }

  isIdle(): boolean {
    return (
      !this.uploading &&
      this.entries.every(
        (e) =>
          e.status === 'uploaded' ||
          e.status === 'failed' ||
          e.status === 'dropped'
      )
    );
  }

  kick(): void {
    if (this.disposed) {
      return;
    }
    void this.pump();
  }

  private async pump(): Promise<void> {
    if (this.uploading || this.held) {
      return;
    }
    this.uploading = true;
    try {
      while (!this.held) {
        while (
          this.cursor < this.entries.length &&
          this.entries[this.cursor].status !== 'queued'
        ) {
          this.cursor += 1;
        }
        const entry = this.entries[this.cursor];
        if (!entry || !this.options.canUpload(entry)) {
          break;
        }
        const carriesFinal = entry.isFinal;
        entry.status = 'uploading';
        entry.attempts += 1;
        try {
          await this.options.upload(entry);
        } catch (error) {
          if (isRetryableUploadError(error, entry.attempts)) {
            entry.status = 'queued';
            if (!this.degraded) {
              this.degraded = true;
              this.options.onDegraded?.(entry);
            }
            logWarn('uploadQueue', 'Chunk upload failed; will retry', {
              chunkNumber: entry.chunkNumber,
              attempts: entry.attempts,
              error: String(error),
            });
            this.scheduleBackoff(entry.attempts);
            return;
          }
          entry.status = 'failed';
          this.release(entry);
          counters.nonRetryableFailures += 1;
          logError(
            'uploadQueue',
            'Chunk upload failed permanently (non-retryable); continuing with the next chunk',
            {
              chunkNumber: entry.chunkNumber,
              attempts: entry.attempts,
              error: String(error),
            }
          );
          this.options.onSettled(entry, 'failed', error);
          this.notifyRunWaiters();
          continue;
        }
        entry.status = 'uploaded';
        entry.finalDelivered = carriesFinal;
        if (entry.attempts > 1) {
          counters.retryRecoveries += 1;
        }
        if (this.degraded) {
          this.degraded = false;
          this.options.onRecovered?.(entry);
        }
        if (
          this.options.releaseOnUpload(entry) ||
          this.closed ||
          entry.transcriptReceived
        ) {
          this.release(entry);
        }
        this.options.onSettled(entry, 'uploaded');
        // Per-chunk, not per-pump: a run whose chunks are all acked must be
        // released even while the pump is still uploading a LATER run's backlog.
        this.notifyRunWaiters();
      }
    } finally {
      this.uploading = false;
      this.notifyRunWaiters();
    }
    this.maybeDispose();
  }

  /** Full jitter: delay ∈ [0, min(BACKOFF_MAX_MS, BACKOFF_BASE_MS · 2^(attempts−1))]. */
  private scheduleBackoff(attempts: number): void {
    if (this.backoffTimer !== null) {
      clearTimeout(this.backoffTimer);
    }
    const cap = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (attempts - 1));
    this.backoffTimer = window.setTimeout(() => {
      this.backoffTimer = null;
      this.kick();
    }, Math.random() * cap);
  }

  private evictForBytes(incomingBytes: number): void {
    while (this.unackedBytes + incomingBytes > this.maxUnackedBytes) {
      const victim = this.entries.find(
        (e) => !e.released && (e.status === 'queued' || e.status === 'uploaded')
      );
      if (!victim) {
        break;
      }
      const wasUploaded = victim.status === 'uploaded';
      if (!wasUploaded) {
        victim.status = 'dropped';
      }
      this.release(victim);
      const details = {
        chunkNumber: victim.chunkNumber,
        bytes: victim.size,
        boundBytes: this.maxUnackedBytes,
      };
      // An already-uploaded victim is only bytes being reclaimed — the server
      // owns that audio — so it is counted and logged apart from a genuine drop.
      // Conflating the two makes every long visit look like it lost audio.
      if (wasUploaded) {
        counters.releasedAtBound += 1;
        logWarn(
          'uploadQueue',
          'Un-acked audio buffer exceeded its bound; released an already-uploaded chunk (no audio lost)',
          details
        );
      } else {
        counters.droppedAtBound += 1;
        logError(
          'uploadQueue',
          'Un-acked audio buffer exceeded its bound; dropped the oldest un-acked chunk',
          details
        );
        this.options.onSettled(victim, 'dropped');
      }
    }
  }

  private release(entry: QueuedChunk<TRun>): void {
    if (entry.released) {
      return;
    }
    entry.released = true;
    entry.blob = null;
    this.unackedBytes -= entry.size;
  }

  private maybeDispose(): void {
    if (this.disposed || !this.closed || !this.isIdle()) {
      return;
    }
    this.disposed = true;
    window.removeEventListener('online', this.handleOnline);
    window.clearInterval(this.retryInterval);
    if (this.backoffTimer !== null) {
      clearTimeout(this.backoffTimer);
      this.backoffTimer = null;
    }
  }
}
